/**
 * RemoteBot.js — frontend proxy for the server-side bot runner.
 * ─────────────────────────────────────────────────────────────
 * Mirrors the slice of the OrderflowBot interface that
 * BotSettingsMenu / BotBadge / the chart actually use, but every
 * mutation becomes an { action:"bot_command" } over the existing
 * app WebSocket, and all reads come from cached bot_status
 * broadcasts. No new socket, no polling.
 *
 * Wire-up (chart):
 *   const remote = new RemoteBot(() => wsRef.current)
 *   remote.attach(wsRef.current)   // re-attach on reconnect
 *   remote.setContext({ token, interval })
 */

export class RemoteBot {
  constructor(getWs, seedConfig = {}) {
    this._getWs = getWs
    this._status = {}        // last bot_status payload (verbatim)
    this._listener = null
    this._attachedWs = null
    this.online = false      // runner connected server-side?
    this.enabled = false
    this.state = 'SCANNING'
    // Seed with a COMPLETE config (caller passes the local bot's) — the
    // menu's knob rows call .toFixed() on config-derived values on open,
    // so an empty config crashes it before the first server broadcast.
    this.config = { ...seedConfig }
    // Menu's onReset writes these.
    this.tradeLog = []
    this._dailyPnL = 0
    this._dailyTradeCount = 0
    this._ctx = { token: null, interval: 5, bucket: undefined }
    // Server journal cache (chronological, as stored server-side). Filled
    // by the one-shot `journal` field the runner attaches to bot_status
    // in reply to { cmd:'journal' }.
    this._journal = []
    this._lastExitSeen = 0
    // Journal-delivery watchdog. attach() asks for the journal once, but
    // that request is only meaningful if it actually reached the wire —
    // and attach() commonly runs while the socket is still CONNECTING.
    // These track "did a journal array ever come back" so the status
    // listener can re-ask instead of leaving the chart's bot markers and
    // the journal panel permanently empty. (The old behavior: the initial
    // request was silently dropped and nothing re-requested until the next
    // EXIT, which on a quiet bot could be hours.)
    this._journalSeen = false
    this._journalReqAt = 0
  }

  /** Chart keeps this current so enable() knows what to watch. */
  setContext({ token, interval, bucket } = {}) {
    if (token !== undefined) this._ctx.token = token
    if (Number.isFinite(interval)) this._ctx.interval = interval
    if (Number.isFinite(bucket)) this._ctx.bucket = bucket
  }

  /** Attach the bot_status listener. Safe to call repeatedly / on reconnect. */
  attach(ws) {
    if (!ws || ws === this._attachedWs) return
    if (this._attachedWs && this._listener) {
      try { this._attachedWs.removeEventListener('message', this._listener) } catch {}
    }
    this._listener = (ev) => {
      let msg
      try { msg = JSON.parse(ev.data) } catch { return }
      if ((msg.type || msg.action) !== 'bot_status') return
      this.online = msg.online !== false
      if (!this.online) { this.enabled = false; return }
      this._status = msg
      this.enabled = !!msg.enabled
      this.state = msg.state || this.state
      const cfg = msg.config || msg.status?.config
      if (cfg) this.config = { ...this.config, ...cfg }
      // One-shot journal rider (reply to { cmd:'journal' }).
      if (Array.isArray(msg.journal)) {
        this._journal = msg.journal
        this._journalSeen = true
      }
      // Entry-skip log rides the same one-shot reply (newest last).
      if (Array.isArray(msg.skips)) this._skips = msg.skips
      // Any slot closing a trade means the journal grew — refresh it. The
      // reply carries the same lastSignal times, so this can't loop.
      const exits = [msg.lastSignal, ...(msg.slots || []).map(s => s?.lastSignal)]
        .filter(s => s && s.type === 'EXIT')
      const latest = Math.max(0, ...exits.map(s => s.time || 0))
      if (latest > this._lastExitSeen) {
        this._lastExitSeen = latest
        this._requestJournal()
      } else if (!this._journalSeen && Date.now() - this._journalReqAt > 10000) {
        // We're clearly talking to a live runner (this IS a status frame)
        // but no journal has ever arrived — the attach-time request was
        // dropped on a not-yet-open socket. Ask again, at most every 10s,
        // and stop the moment one lands.
        this._requestJournal()
      }
    }
    ws.addEventListener('message', this._listener)
    this._attachedWs = ws
    // Ask for current state so the UI isn't blank until the next 10s push.
    // Deferred to socket-open when needed: attach() runs from a React effect
    // the moment wsRef.current exists, which is typically readyState 0.
    this._sendWhenOpen({ cmd: 'status' })
    this._requestJournal()
  }

  _send(payload) {
    const ws = this._getWs?.()
    if (!ws || ws.readyState !== 1) return false
    ws.send(JSON.stringify({ action: 'bot_command', ...payload }))
    return true
  }

  /** Send now if the socket is open, else once it opens. Used only for the
   *  attach-time bootstrap reads — mutations stay fire-and-forget so a
   *  queued enable() can't fire against a socket the user has moved on from. */
  _sendWhenOpen(payload) {
    if (this._send(payload)) return true
    const ws = this._getWs?.()
    if (!ws || ws.readyState !== 0) return false   // closed/closing — give up
    const onOpen = () => {
      try { ws.removeEventListener('open', onOpen) } catch {}
      if (ws === this._attachedWs) this._send(payload)
    }
    ws.addEventListener('open', onOpen)
    return false
  }

  _requestJournal() {
    this._journalReqAt = Date.now()
    this._sendWhenOpen({ cmd: 'journal' })
  }

  /** Menu ON: watch the charted token and arm the bot. */
  enable() {
    const { token, interval, bucket } = this._ctx
    // Discovery owns targeting while it's hunting. Arming from the menu
    // used to ship a watch for the CHARTED token alongside the enable —
    // which yanked the bot off its discovery pick onto whatever (possibly
    // dead) coin the user happened to be charting. Retarget explicitly by
    // turning Hunt off first, or watch stays with the hunt.
    if (token && !this._status?.discovery) this._send({ cmd: 'watch', token, interval, bucket })
    this._send({ cmd: 'enable' })
    this.enabled = true            // optimistic; corrected by next status
  }

  disable() {
    this._send({ cmd: 'disable' })
    this.enabled = false
  }

  stop() { this._send({ cmd: 'stop' }); this.enabled = false }

  updateConfig(cfg) {
    this.config = { ...this.config, ...cfg }
    this._send({ cmd: 'config', config: cfg })
  }

  setDryRun(value) { this._send({ cmd: 'dryrun', value: !!value }) }

  setDiscovery(value) { this._send({ cmd: 'discover', value: !!value }) }

  setAnalysis({ intervalSec, bucket, entryDz } = {}) {
    // entryDz is bot config, not an envelope setting — the runner's
    // 'analysis' command never read it, so it went nowhere. Route it as config.
    if (entryDz !== undefined) this.updateConfig({ entryDz })
    if (intervalSec !== undefined || bucket !== undefined) this._send({ cmd: 'analysis', intervalSec, bucket })
  }

  /** Settings-only retune — value deliberately omitted so the hunt state
   *  is untouched (runner only flips enabled when value is present). */
  setDiscoverySettings(settings) { this._send({ cmd: 'discover', settings }) }

  /** Menu reads this every tick — return the server bot's own getStatus()
   *  payload merged onto a COMPLETE zero-filled shape. The menu calls
   *  .toFixed() on churnScore/dailyPnL unconditionally, and reads nested
   *  activeBase/pilot/load only behind null checks — so top-level numbers
   *  must always be defined, nested objects default to null, exactly like
   *  the local bot's getStatus() before any data. */
  /** All slots from the last bot_status (empty on pre-coordinator runners). */
  getSlots() { return Array.isArray(this._status.slots) ? this._status.slots : [] }

  /** Release one slot by token (server puts the token on cooldown). */
  stopSlot(token) { if (token) this._send({ cmd: 'stopSlot', token }) }

  getStatus() {
    // Point the single-bot interface at the charted token's slot when one
    // exists, else slot 0 — the Pip-Boy tracks whatever coin you're on.
    // Pre-coordinator payloads (no slots[]) fall through to the top-level
    // mirror, which IS slot 0.
    const list = Array.isArray(this._status.slots) && this._status.slots.length
      ? this._status.slots : null
    const sl = list
      ? (this._ctx.token && list.find(x => x.token === this._ctx.token))
        || list.find(x => x.slot === 0) || list[0]
      : null
    const s = (sl ? sl.status : this._status.status) || {}
    return {
      state:            (sl ? sl.state : this._status.state) || this.state,
      enabled:          this.enabled,
      cooldownRemaining: 0,
      dailyPnL:         0,
      dailyTradeCount:  0,
      activeBase:       null,
      pilot:            null,
      load:             null,
      currentToken:     null,  // set AFTER the spread — see below
      tapeHeat:         0,
      churnScore:       0,
      vacuumScore:      0,
      approachVelocity: 0,
      densityRatio:     0,
      ...s,             // server values override the defaults when present
      // The runner's top-level token = what it's ACTUALLY watching. The
      // bot's internal currentToken (inside s) only updates when update()
      // runs — a blind bot (candles < 6 on a fresh pick) reports the LAST
      // coin it saw, which painted a stale token on the Pip-Boy.
      currentToken: (sl ? sl.token : this._status.token) || s.currentToken || null,
      // Runner-level flags live at the payload top, not inside getStatus():
      discovery: !!this._status.discovery,
      dryRun:    this._status.dryRun !== false,   // unknown → assume dry (safe)
      intervalSec: (sl ? sl.intervalSec : this._status.intervalSec) ?? 5,
      envBucket:   (sl ? sl.bucket : this._status.envBucket) ?? null,
      lastScan:       this._status.lastScan || null,
      watchSource:    (sl ? sl.source : this._status.watchSource) || 'manual',
      watchStartedAt: (sl ? sl.watchStartedAt : this._status.watchStartedAt) || 0,
      watchPinned:    sl ? !!s.activeBase : !!this._status.watchPinned,
      discoveryCfg:   this._status.discoveryCfg || null,
      coordinatorCfg: this._status.coordinatorCfg || null,
    }
  }

  /** Newest first, like OrderflowBot.getJournal(). Entries are tagged
   *  { token, slot } by the runner for cross-position stats. */
  getJournal() { return [...this._journal].reverse() }
  /** Entry-gate skips (newest first), from the last journal reply. */
  getSkips() { return [...(this._skips || [])].reverse() }
  resetJournal() {
    const n = this._journal.length
    this._journal = []
    this._send({ cmd: 'resetJournal' })
    return n
  }
  serialize() { return null }
}

export default RemoteBot
